/**
 * Fear & Greed rating bands — the single definition of where a 0-100 sentiment
 * score stops being fear and starts being greed.
 *
 * Lives in `src/shared` (rather than `workers/shared`) because BOTH trees need
 * it and only this direction works: CRA's module scope forbids the frontend
 * importing from outside `src/`, while wrangler happily bundles frontend-tree
 * files into a worker. Same arrangement as `src/shared/bandSignals.js`.
 *
 * Consumers:
 *  - `workers/shared/sectorSentiment.js` — rates each sector's composite score
 *  - `src/components/SectorFearGreedMap.jsx` — colours each component sub-score
 *  - `src/shared/earningsSetup.js` — rates the Expectations Bar, own bands
 *  - `src/utils/briefVisuals.js` — the mobile Brief's Fear & Greed meter
 *
 * Thresholds match the market-wide CNN gauge so the two read consistently side
 * by side. Changing them changes stored ratings — see issue #92.
 */

export const RATING_BANDS = [
	{ maxExclusive: 25, rating: 'extreme fear' },
	{ maxExclusive: 45, rating: 'fear' },
	{ maxExclusive: 55, rating: 'neutral' },
	{ maxExclusive: 75, rating: 'greed' },
	{ maxExclusive: Infinity, rating: 'extreme greed' }
];

/**
 * Label a 0-100 score against an ascending band table. The `bands` parameter is
 * the one definition of how a band array is read (`maxExclusive`, ordered low to
 * high, last band open-ended), so scales other than fear & greed — the
 * Expectations Bar's, for instance — reuse this rather than restating the
 * lookup.
 *
 * @param {number} score 0-100
 * @param {Array<{maxExclusive: number, rating: string}>} [bands]
 * @returns {string} rating band label
 */
export function ratingFor(score, bands = RATING_BANDS) {
	return bands.find(band => score < band.maxExclusive).rating;
}

/**
 * One colour per rating band, shared by every surface that draws a score: the sector
 * map's component bars and the mobile Brief's meter. `bar` fills a mark, `text` labels
 * it, `bg` tints the rest of that band's track.
 */
export const RATING_COLORS = {
	'extreme fear': { text: '#dc2626', bar: '#dc2626', bg: '#fef2f2' },
	fear: { text: '#ea580c', bar: '#f97316', bg: '#fff7ed' },
	neutral: { text: '#4b5563', bar: '#9ca3af', bg: '#f3f4f6' },
	greed: { text: '#059669', bar: '#10b981', bg: '#f0fdf4' },
	'extreme greed': { text: '#065f46', bar: '#047857', bg: '#ecfdf5' }
};

export const colorsFor = (rating) => RATING_COLORS[rating?.toLowerCase()] || RATING_COLORS.neutral;
